// Feb-2026 · Frontend cost calculator (mirror of backend budget_service).
//
// Estimates USD spend for a given (model, word_count) pair, plus the
// per-10K-word "meter rate" used throughout the mix-and-match UI.
//
// Reference: at Sonnet 4.5 rates, ~$13.97 buys 500K words of text
// (input+output blended, novel context). Adjacent costs (Nano-Banana
// covers, Copyscape scans, infra) add $14.30/500K on top. We surface
// text and adjacent separately so the UI can show a clean breakdown.

// Words ↔ tokens: 500K words ≈ 665K output + 1.33M input tokens.
// At Sonnet 4.5 rates ($3 in / $15 out per 1M):
//    input:  1.33M × $3  = $3.99
//    output: 665K × $15  = $9.98
//    total (500K words)  = $13.97
const SONNET_TEXT_PER_500K = 13.97;
const ADJACENT_PER_500K    = 14.30;

// Effective markup applied to raw COGS before Stripe fees.
// Read from app_config.pricing_markup_pct once wired; default here
// tracks the current admin default.
export const DEFAULT_MARKUP_PCT = 30.0;
const STRIPE_FEE_PCT       = 2.9;
const STRIPE_FEE_FLAT_USD  = 0.30;

/** Raw pre-markup cost for a given word count on a specific model. */
export function estimateCogsUsd(words, model) {
  const w    = Math.max(0, Number(words) || 0);
  const mult = Number(model?.cost_multiplier) || 1.0;
  const text     = (SONNET_TEXT_PER_500K / 500_000) * w * mult;
  const adjacent = (ADJACENT_PER_500K   / 500_000) * w;
  return { text, adjacent, total: text + adjacent };
}

/**
 * Sticker price with markup + Stripe fees folded in. `markupPct` is
 * the admin-controlled setting (default 30%).
 */
export function estimatePriceUsd(words, model, { markupPct = DEFAULT_MARKUP_PCT } = {}) {
  const { total: cogs } = estimateCogsUsd(words, model);
  const withMarkup = cogs * (1.0 + markupPct / 100.0);
  const withStripe = (withMarkup + STRIPE_FEE_FLAT_USD) / (1.0 - STRIPE_FEE_PCT / 100.0);
  return {
    cogs_usd:       cogs,
    marked_up_usd:  withMarkup,
    final_price_usd: withStripe,
  };
}

/** Convenience: sticker price per 10K words at this model. */
export function pricePer10K(model, opts) {
  return estimatePriceUsd(10_000, model, opts).final_price_usd;
}

/** Convenience: sticker price per 500K words at this model. */
export function pricePer500K(model, opts) {
  return estimatePriceUsd(500_000, model, opts).final_price_usd;
}

/** Format a USD number for compact display. */
export function fmtUsd(n) {
  const v = Number(n) || 0;
  if (v < 0.10) return `$${v.toFixed(3)}`;
  if (v < 10)   return `$${v.toFixed(2)}`;
  if (v < 100)  return `$${v.toFixed(2)}`;
  return `$${Math.round(v)}`;
}

/**
 * Detection-evasion score (0–100) for each model, sourced from the
 * strategy analysis: "reader threshold of 70% AI-recognition".
 * Higher = better at clearing the threshold without human polish.
 */
export const DETECTION_SCORES = {
  'gemini-3-flash':   45,
  'claude-sonnet-4-5': 68,
  'gpt-5-2':           62,
  'gemini-3-1-pro':    70,
  'gpt-5-4':           76,
  'claude-opus-4-7':   80,
  'deepseek-flash':    50,
};

/** Best-use blurb per model (surfaced in the model picker popover). */
export const BEST_USE = {
  'gemini-3-flash':    'Rapid drafts and brainstorming. Needs a human or Opus rewrite pass before publishing.',
  'claude-sonnet-4-5': 'Solid all-round drafter. Sweet spot for chapter-level work when Opus is overkill.',
  'gpt-5-2':           'Default engine. Best balance of quality vs cost for full novel drafting.',
  'gemini-3-1-pro':    'Strongest plot logic and continuity. Good for thrillers and sci-fi.',
  'gpt-5-4':           'Premium OpenAI. Excellent dialogue and structural editing.',
  'claude-opus-4-7':   'The gold standard for emotion, subtext, and publish-ready prose.',
  'deepseek-flash':    'Cheapest option for drafts and brainstorming — needs your own DeepSeek key.',
};
